/**
@format
@flow local-strict
*/
import React, { useState, useRef, useEffect } from 'react';
import { Auth } from 'aws-amplify';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import ActionTypes from '@ziynx/action-types';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { Button } from '@ziynx/components/buttons';
import { eventNames } from '@ziynx/analytics';
import Colors from '@ziynx/theme/colors';
import BannerNotification from '@ziynx/components/notifications';
import Input from '@ziynx/components/inputs';
import { ContentContainer } from '@ziynx/components/layout';
import { Form, FormRow } from '@ziynx/components/forms';
import { withAppManager } from '@ziynx/components/utilities';
import type { Viewports } from '@ziynx/flow-types/globals';

type Props = $ReadOnly<{|
  /**
   * Navigation stack
   */
  history: Object,
  /**
   * Viewports
   */
  viewport: Viewports
|}>;

const PageWrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  max-width: 688px;
  padding: ${({ isMobile }) => (isMobile ? '40px 16px' : '40px')};
  ${({ isMobile }) => isMobile && 'margin: auto;'}
`;

const PageContentWrapper = styled.div`
  width: 100%;
  max-width: 688px;
  ${'' /* max-width: ${({ isMobile }) => (isMobile ? '100%' : '688px')}; */}
`;

const StyledButton = styled(Button)`
  width: ${({ isMobileSmall }) => (isMobileSmall ? '100%' : '50%')};
  &:first-of-type {
    ${({ isMobileSmall }) => (isMobileSmall ? 'margin-bottom: 16px;' : 'margin-right: 16px;')}
  }
`;

const StyledButtonSection = styled.div`
  display: flex;
  flex-direction: ${({ isMobileSmall }) => (isMobileSmall ? 'column' : 'row')};
  justify-content: space-between;
  align-items: center;
  margin-bottom: 40px;
`;

const StyledFormRow = styled(FormRow)`
  ${({ firstChild }) => firstChild && 'padding-top: 56px;'}
  ${({ isMobileSmall, firstChild }) => firstChild && isMobileSmall && 'padding-top: 52px;'}
`;

const StyledBannerWrapper = styled.div`
  position: fixed;
  top: 0;
  z-index: 996;
  width: 100%;
  flex: 1;
`;

const inputsData = {
  data: [
    {
      id: '1',
      name: 'currentPassword',
      defaultValue: '',
      regExPattern: undefined,
      errorMessage: undefined,
    },
    {
      id: '2',
      name: 'newPassword',
      defaultValue: '',
      regExPattern: /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])[0-9a-zA-Z!@#$%^ˆ&*?_\-+=()\\|<>/{}[\]:;.,~˜`'"]{8,}$/im,
      errorMessage:
        'Password is too weak. Use at least 8 characters and 1 number',
    },
    {
      id: '3',
      name: 'confirmNewPassword',
      defaultValue: '',
      regExPattern: undefined,
      errorMessage: undefined,
    },
  ],
};

/**
 * Time for banner to setTimeout
 */
const bannerTimeOut = 5000;

/**
 * Change password screen after user clicks on change in Access card
 */
const ChangePasswordScreen = (props: Props): React$Node => {
  const {
    handleSubmit,
    control,
    errors,
    setError,
    clearErrors,
    formState,
    watch,
  } = useForm({ mode: 'onTouched' });

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch({
      type: ActionTypes.analytics.logEvent.START,
      name: eventNames.navigated_change_password,
    });
  }, []);

  const { viewport, history } = props;
  const isMobile = viewport === 'mobileSmall' || viewport === 'mobile';
  const isMobileSmall = viewport === 'mobileSmall';

  const newPasswordWatch = useRef({});
  const currentPasswordWatch = useRef({});
  // Watch and store value for new password field
  newPasswordWatch.current = watch('newPassword');
  // Watch and store value for current password field
  currentPasswordWatch.current = watch('currentPassword');

  // Fields to watch for changes or errors
  const { dirtyFields } = formState;

  // When there's an error with amplify
  const [hasError, setHasError] = useState(false);
  // state for banner error message
  const [bannerErrorMessage, setBannerErrorMessage] = useState('');
  // Shows if waiting for response from AWS
  const [isLoading, setIsLoading] = useState(false);
  // State to keep track if banner should be rendered
  const [shouldRenderBanner, setRenderBanner] = useState(false);

  /**
   * Function to signal there's an error
   * and render banner
   */
  const setBannerError = (): void => {
    setHasError(true);
    setRenderBanner(true);
  };

  const goBack = (): void => {
    dispatch({
      type: ActionTypes.analytics.logEvent.START,
      name: eventNames.exited_change_password,
    });
    history.goBack();
  };

  const onSubmit = async (data: Object): Promise<any> => {
    setIsLoading(true);

    // Get data from form submission
    const { currentPassword: oldPassword, newPassword } = data || {};

    Auth.currentAuthenticatedUser()
      .then((user): void => Auth.changePassword(user, oldPassword, newPassword))
      .then((): void => history.push('/settings', { from: 'Change Password', passwordUpdated: true }))
      .then(dispatch({
        type: ActionTypes.analytics.logEvent.START,
        name: eventNames.saved_new_password,
      }))
      .catch((error): void => {
        setRenderBanner(false); // Reset banner state
        setHasError(false);
        switch (error.code) {
        case 'NotAuthorizedException':
        case 'InvalidPasswordException':
        case 'InvalidParameterException':
        // When current password is not correct, missing
        // lengths, or does not meet standard pw constraints
          setError('currentPassword', {
            type: 'manual',
            message: 'Incorrect current password',
          });
          break;

        default:
          setBannerError();
          setBannerErrorMessage(error.message);
          break;
        }
      });
    setIsLoading(false);
  };

  const renderInputs = ({
    onChange, onBlur, onFocus, name,
  }: Object): React$Node => {
    let label = '';
    switch (name) {
    case 'newPassword':
      label = 'New password';
      break;
    case 'confirmNewPassword':
      label = 'Re-enter new password';
      break;
    case 'currentPassword':
    default:
      label = 'Current password';
      break;
    }

    /**
     * Set value of input
     */
    const onInputChange = (
      event: SyntheticInputEvent<HTMLInputElement>,
    ): void => {
      (event?.target: HTMLInputElement);
      onChange(event?.target?.value);
    };

    /**
     * Function to clear any errors of inputs
     * when on focus event, if any
     */
    const onInputFocus = (): any => {
      if (errors?.[name]) clearErrors(name);
      return onFocus;
    };

    return (
      <Input
        variant="password"
        error={!!errors?.[name]}
        errorLabel={(hasError && name === 'currentPassword') ? 'Invalid password' : errors?.[name]?.message}
        inputName={name}
        label={label}
        onChange={onInputChange}
        onBlur={onBlur}
        onFocus={onInputFocus}
        required
      />
    );
  };

  /**
   * Function to map out FormRows
   * that holds the Controller of each types of inputs
   * based on inputsData
   */
  const renderInputsControllers = (): React$Node => inputsData.data.map((field, index) => {
    const rulesDefault = {
      required: 'Please enter a value to continue',
      pattern: {
        value: field?.regExPattern,
        message: field?.errorMessage,
      },
    };

    // Confirms new password matches the re-enter new password
    const rulesMatchPassword = {
      required: 'Please enter a value to continue',
      validate: (value) => value === newPasswordWatch.current || 'The passwords do not match',
    };

    // Checks to see if new password not same as current password
    const rulesNewPassword = {
      required: 'Please enter a value to continue',
      validate: (value) => value !== currentPasswordWatch.current || 'New password cannot be the same as current password',
      pattern: {
        value: field?.regExPattern,
        message: field?.errorMessage,
      },
    };

    /**
     * Function to determine the rules for each inputs
     */
    const getInputRules = (): Object => {
      let rules;
      switch (field.name) {
      case 'confirmNewPassword':
        rules = { rules: rulesMatchPassword };
        break;

      case 'newPassword':
        rules = { rules: rulesNewPassword };
        break;

      case 'currentPassword':
      default:
        rules = { rules: rulesDefault };
        break;
      }
      return rules;
    };

    return (
      <StyledFormRow
        isMobileSmall={isMobileSmall}
        firstChild={index === 0}
        key={field.id}
      >
        <Controller
          control={control}
          render={renderInputs}
          name={field.name}
          defaultValue={field.defaultValue}
          {...getInputRules()}
        />
      </StyledFormRow>
    );
  });

  /**
   * Function to check if dirtyFields length equals to inputsData length.
   * Returns true if not -> disable button
   */
  const isDisabled = (): boolean => {
    if (!inputsData?.data || !dirtyFields || isLoading) return true;
    return Object.keys(dirtyFields).length !== inputsData?.data?.length;
  };

  /**
   * Function to watch if an input has error
   * after user submit upon validation.
   * Errors object is {} by default - which means no error
   * Returns true if length of errors object != 0
   * and false if empty
   */
  const isError = (): boolean => !!Object.keys(errors).length;

  const renderBanner = (
    <StyledBannerWrapper>
      <BannerNotification animated showCloseIcon timeout={bannerTimeOut} variant={hasError ? 'error' : 'success'}>
        {hasError && bannerErrorMessage}
      </BannerNotification>
    </StyledBannerWrapper>
  );

  return (
    <>
      <Helmet>
        <title>Change Password | ZIYNX</title>
      </Helmet>
      {shouldRenderBanner && renderBanner}
      <ContentContainer>
        <PageWrapper isMobile={isMobile}>
          <PageContentWrapper isMobile={isMobile}>
            <Form
              formID="change-password"
              title="Change password"
              width="100%"
              maxWidth={isMobile ? '100%' : '688px'}
              headingPaddingBottom="0px"
            >
              {renderInputsControllers()}

              <StyledButtonSection isMobileSmall={isMobileSmall}>
                <StyledButton
                  isMobileSmall={isMobileSmall}
                  disabled={isDisabled() || isError()}
                  isLoading={isLoading}
                  width="50%"
                  form="change-password"
                  type="submit"
                  onClick={handleSubmit(onSubmit)}
                >
                  Change
                </StyledButton>
                <StyledButton
                  isMobileSmall={isMobileSmall}
                  inverted
                  width="50%"
                  backgroundColor={Colors.White}
                  onClick={goBack}
                >
                  Cancel
                </StyledButton>
              </StyledButtonSection>
            </Form>
          </PageContentWrapper>
        </PageWrapper>
      </ContentContainer>
    </>
  );
};

// $FlowFixMe
export default withAppManager(ChangePasswordScreen);
